import React, { useMemo, useState, useEffect } from "react";
import {
  Button,
  Grid,
  Group,
  SegmentedControl,
  Select,
  Text,
  TextInput,
  NumberInput,
  useMantineTheme,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import dayjs from "dayjs";
import { FormList } from "@mantine/form";
import { UseFormReturnType } from "@mantine/form/lib/use-form";
import AvailableField from "../services/model/AvailableField";
import SegmentRule from "../services/model/SegmentRule";
import {
  getOperatorLabel,
  getDateOperatorLabel,
  VALUE_LESS_OPERATORS,
  RELATIVE_DATE_OPERATORS,
  EXACT_DATE_OPERATORS,
  RELATIVE_DATE_PATTERN,
} from "../constants/segmentConstants";
import RelativeDateInput from "../elements/RelativeDateInput";

type RuleConditionProps = {
  index: number;
  form: UseFormReturnType<{
    name: string;
    description: string;
    rules: FormList<SegmentRule>;
    logic_operator: "AND" | "OR";
    is_active: boolean;
  }>;
  availableFields: AvailableField[];
  onRemove: (index: number) => void;
  onAdd: () => void;
  canRemove: boolean;
};

const RuleCondition: React.FC<RuleConditionProps> = ({
  index,
  form,
  availableFields,
  onRemove,
  onAdd,
  canRemove,
}) => {
  const theme = useMantineTheme();
  const primaryColor = theme.colors["jem-theme"][5];
  const accentColor = theme.fn.rgba(theme.colors["jem-theme"][0], 0.5);

  const currentRule = form.values.rules[index];

  const selectedField = useMemo(() => {
    const fieldValue = currentRule?.field;
    return availableFields.find((f) => f.field === fieldValue);
  }, [currentRule?.field, availableFields]);

  // Memoized detection of date mode from current value (value takes priority)
  const detectedDateMode = useMemo((): boolean => {
    const value = String(currentRule?.value || "");

    // If value is a relative date pattern (e.g., "30d"), we're in relative mode
    if (value && RELATIVE_DATE_PATTERN.test(value)) {
      return false;
    }

    // If value is empty, default to relative mode
    if (!value) {
      return false;
    }

    // If value is a valid date string, we're in exact mode
    if (dayjs(value).isValid()) {
      return true;
    }

    // Default to relative mode
    return false;
  }, [currentRule?.value]);

  // Use local state to track exact date mode, initialized from detected mode
  const [isExactDateMode, setIsExactDateMode] = useState(detectedDateMode);

  // Sync mode when field changes or when loading existing data
  useEffect(() => {
    if (selectedField?.type === "date") {
      setIsExactDateMode(detectedDateMode);
    }
  }, [currentRule?.field, detectedDateMode, selectedField?.type]);

  const fieldOptions = useMemo(
    () =>
      availableFields.map((field) => ({
        value: field.field,
        label: field.label,
      })),
    [availableFields]
  );

  const operatorOptions = useMemo(() => {
    if (!selectedField) return [];

    // For date fields, filter operators based on exact/relative mode
    if (selectedField.type === "date") {
      const allowedOps: readonly string[] = isExactDateMode
        ? EXACT_DATE_OPERATORS
        : RELATIVE_DATE_OPERATORS;
      return selectedField.operators
        .filter((op) => allowedOps.includes(op))
        .map((op) => ({
          value: op,
          // Use date-specific labels for relative mode (e.g., "More than" instead of "Greater than")
          label: isExactDateMode
            ? getOperatorLabel(op)
            : getDateOperatorLabel(op),
        }));
    }

    return selectedField.operators.map((op) => ({
      value: op,
      label: getOperatorLabel(op),
    }));
  }, [selectedField, isExactDateMode]);

  const showValueInput = useMemo(() => {
    // Hide value input immediately for boolean fields
    if (selectedField?.type === "boolean") {
      return false;
    }
    const operator = currentRule?.operator;
    return !VALUE_LESS_OPERATORS.includes(
      operator as (typeof VALUE_LESS_OPERATORS)[number]
    );
  }, [currentRule?.operator, selectedField?.type]);

  const handleFieldChange = (value: string | null): void => {
    // Check if the newly selected field is a date type
    const newField = availableFields.find((f) => f.field === value);
    const isDateField = newField?.type === "date";

    const newRule: SegmentRule = {
      field: value || "",
      operator: "",
      // Initialize date fields with "1d" for relative mode default
      value: isDateField ? "1d" : "",
    };
    form.setListItem("rules", index, newRule);

    // Reset to relative mode for date fields
    if (isDateField) {
      setIsExactDateMode(false);
    }
  };

  const handleOperatorChange = (value: string | null): void => {
    const newRule: SegmentRule = {
      ...currentRule,
      operator: value || "",
    };
    form.setListItem("rules", index, newRule);
  };

  const handleValueChange = (
    value: string | number | undefined | null
  ): void => {
    const newRule: SegmentRule = {
      ...currentRule,
      value: value ?? "",
    };
    form.setListItem("rules", index, newRule);
  };

  const handleDateModeChange = (mode: string): void => {
    const isExact = mode === "exact";
    setIsExactDateMode(isExact);
    // Reset operator and value when switching modes
    const newRule: SegmentRule = {
      ...currentRule,
      operator: "",
      value: isExact ? "" : "1d", // Default to "1d" for relative mode
    };
    form.setListItem("rules", index, newRule);
  };

  const renderValueInput = (): JSX.Element | null => {
    if (!showValueInput) {
      return null;
    }

    // Dropdown for fields with predefined options
    if (selectedField?.options && selectedField.options.length > 0) {
      const selectData = selectedField.options.map((opt) => {
        if (typeof opt === "string") {
          return { value: opt, label: opt };
        }
        // Backend returns { key, label } format
        return { value: opt.key ?? opt.label, label: opt.label };
      });
      return (
        <Select
          placeholder="Select value"
          data={selectData}
          searchable
          value={(currentRule?.value as string) || null}
          onChange={handleValueChange}
        />
      );
    }

    // Date inputs for date fields
    if (selectedField?.type === "date") {
      if (isExactDateMode) {
        // Exact date mode: show DatePicker
        const parsedDate = currentRule?.value
          ? dayjs(String(currentRule.value))
          : null;
        const dateValue =
          parsedDate && parsedDate.isValid() ? parsedDate.toDate() : null;

        return (
          <DatePicker
            placeholder="Select date"
            clearable
            value={dateValue}
            onChange={(date: Date | null) => {
              handleValueChange(date ? dayjs(date).format("YYYY-MM-DD") : "");
            }}
          />
        );
      }

      // Relative date mode: show number + unit inputs
      return (
        <RelativeDateInput
          value={String(currentRule?.value || "")}
          operator={currentRule?.operator}
          onChange={(val) => handleValueChange(val)}
        />
      );
    }

    // Number input for number fields
    if (selectedField?.type === "number") {
      const numericValue = (() => {
        if (typeof currentRule?.value === "number") {
          return currentRule.value;
        }
        if (
          typeof currentRule?.value === "string" &&
          currentRule.value !== ""
        ) {
          const parsed = parseFloat(currentRule.value);
          return Number.isNaN(parsed) ? undefined : parsed;
        }
        return undefined;
      })();

      return (
        <NumberInput
          placeholder="Enter value"
          value={numericValue}
          onChange={handleValueChange}
        />
      );
    }

    // Default: TextInput for string fields
    return (
      <TextInput
        placeholder="Enter value"
        value={String(currentRule?.value || "")}
        onChange={(event) => handleValueChange(event.currentTarget.value)}
      />
    );
  };

  return (
    <Grid align="flex-end" gutter="sm">
      <Grid.Col span={3}>
        <Select
          label={index === 0 ? "Field" : undefined}
          placeholder="Select field"
          data={fieldOptions}
          searchable
          value={currentRule?.field || null}
          onChange={handleFieldChange}
        />
      </Grid.Col>
      <Grid.Col span={3}>
        <Select
          label={index === 0 ? "Rule" : undefined}
          placeholder="Select rule"
          data={operatorOptions}
          disabled={!selectedField}
          value={currentRule?.operator || null}
          onChange={handleOperatorChange}
        />
      </Grid.Col>
      <Grid.Col span={3}>
        {index === 0 && showValueInput && (
          <Text
            component="span"
            size="sm"
            weight={500}
            style={{ display: "block", marginBottom: "4px" }}
          >
            Value
          </Text>
        )}
        {selectedField?.type === "date" && showValueInput && (
          <SegmentedControl
            size="xs"
            value={isExactDateMode ? "exact" : "relative"}
            onChange={handleDateModeChange}
            data={[
              { value: "relative", label: "Time period" },
              { value: "exact", label: "Specific date" },
            ]}
            mb="xs"
            fullWidth
          />
        )}
        {renderValueInput()}
      </Grid.Col>
      <Grid.Col span={3}>
        <Group spacing="xs" position="right">
          <Button
            size="xs"
            radius={6}
            onClick={onAdd}
            style={{
              backgroundColor: accentColor,
              color: primaryColor,
              fontWeight: 600,
              border: "none",
            }}
          >
            Add
          </Button>
          {canRemove && (
            <Button
              size="xs"
              variant="outline"
              radius={6}
              onClick={() => onRemove(index)}
              styles={{
                root: {
                  borderColor: primaryColor,
                  color: primaryColor,
                  fontWeight: 600,
                },
              }}
            >
              Remove
            </Button>
          )}
        </Group>
      </Grid.Col>
    </Grid>
  );
};

export default RuleCondition;
